// Create new file: src/components/modals/AppointmentDayScheduleModal.js

import React, { useEffect, useState } from 'react';
import { X, Clock } from 'lucide-react';
import { useAppointmentsPG } from '../../hooks/useAppointmentsPG';
import { systemConfigService } from '../../services/systemConfig';
import { useBusinessConfig } from '../../context/BusinessConfigContext';

export function AppointmentDayScheduleModal({ isOpen, onClose, targetDate, highlightAppointmentId }) {
  const { allAppointments, pendingAppointments, loading, ensureDateRangeLoaded } = useAppointmentsPG();
  const [dayAppointments, setDayAppointments] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);

  const { settings } = useBusinessConfig();

  useEffect(() => {
    if (settings && settings.workingHours) {
      const slots = systemConfigService.generateTimeSlots(settings.workingHours.startTime, settings.workingHours.endTime);
      setTimeSlots(slots);
    }
  }, []);

  useEffect(() => {
    if (targetDate && ensureDateRangeLoaded) {
      ensureDateRangeLoaded(targetDate);
    }
  }, [targetDate, ensureDateRangeLoaded]);

  useEffect(() => {
    const appointments = [...allAppointments, ...pendingAppointments]
    if (targetDate && appointments) {
      const filtered = appointments.filter(apt =>
        apt.date === targetDate && apt.status !== 'cancelled'
      );
      setDayAppointments(filtered);
    }
  }, [targetDate, allAppointments, pendingAppointments]);

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Get appointments for a specific time (primary slot only - appointments that START at this time)
  const getPrimaryAppointmentsForSlot = (time) => {
    return dayAppointments.filter(apt => apt.time === time);
  };

  // Check if a slot is occupied by any appointment (including secondary slots)
  const isSlotOccupied = (time) => {
    const currentTimeIndex = timeSlots.indexOf(time);
    return dayAppointments.some(apt => {
      const startIndex = timeSlots.indexOf(apt.time);
      const slotsNeeded = Math.ceil(apt.duration / 30);
      const endIndex = startIndex + slotsNeeded - 1;
      return currentTimeIndex >= startIndex && currentTimeIndex <= endIndex;
    });
  };

  // Get stack ID for positioning
  const getStackId = (time) => time;

  // Get stack position for an appointment (matches Schedule.js logic)
  const getStackPosition = (appointment, allAppointments, stackId) => {
    return allAppointments.findIndex(apt => apt.id === appointment.id);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

        <div className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl w-full max-w-md border border-white/20">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200/50">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-pink-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                יומן {new Date(targetDate).toLocaleDateString('he-IL', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100/60 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Schedule Grid */}
          <div className="max-h-96 overflow-y-auto p-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">טוען...</div>
            ) : (
              <div className="space-y-0">
                {timeSlots.map((time) => (
                  <div key={time} className="relative h-12 border-b border-gray-100">
                    {/* Time Label */}
                    <div className="absolute left-0 top-0 w-16 h-full flex items-center px-2 bg-gray-50 border-r border-gray-200 text-sm font-medium text-gray-700 z-30">
                      {formatTime(time)}
                    </div>

                    {/* Appointment Area */}
                    <div className="ml-16 relative h-full">
                      {(() => {
                        const primaryAppointments = getPrimaryAppointmentsForSlot(time);
                        const isOccupied = isSlotOccupied(time);
                        const stackId = getStackId(time);

                        if (primaryAppointments.length > 0) {
                          return primaryAppointments.map((appointment, appointmentIndex) => {
                            const slotsNeeded = Math.ceil(appointment.duration / 30);
                            const stackPosition = getStackPosition(appointment, primaryAppointments, stackId);
                            const isTopCard = stackPosition === primaryAppointments.length - 1;
                            const offsetAmount = 8;
                            const isPending = appointment.status === 'pending_approval';
                            const isHighlighted = appointment.id === highlightAppointmentId;

                            return (
                              <div
                                key={appointment.id}
                                className={`absolute p-2 rounded-lg text-xs cursor-pointer overflow-hidden transition-all duration-200 ${isHighlighted
                                  ? 'bg-gradient-to-r from-amber-100 to-orange-100 border-2 border-amber-500 shadow-lg text-gray-900'
                                  : 'bg-gradient-to-r from-pink-100 to-purple-100 border border-pink-200 text-gray-900'
                                  } ${isTopCard ? 'shadow-lg' : 'shadow-md'}`}
                                style={{
                                  height: `${slotsNeeded * 48}px`,
                                  zIndex: 20 + stackPosition,
                                  top: `${stackPosition * offsetAmount}px`,
                                  left: `${stackPosition * offsetAmount}px`,
                                  right: `${stackPosition * offsetAmount}px`,
                                  bottom: `${stackPosition * offsetAmount}px`,
                                  transform: isTopCard ? 'scale(1)' : 'scale(0.98)',
                                  filter: isTopCard ? 'brightness(1)' : 'brightness(0.9)',
                                }}
                              >
                                <div className="flex items-start justify-between h-full">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">
                                      {appointment.customer_name}
                                    </div>
                                    <div className="text-xs opacity-90 truncate mt-1">
                                      {appointment.service_name}
                                    </div>
                                    <div className="text-xs opacity-75 truncate mt-1">
                                      {formatTime(appointment.time)} • {appointment.duration}ד'
                                    </div>
                                  </div>

                                  {isPending && (
                                    <div className="flex-shrink-0 ml-2">
                                      <div className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full font-medium">
                                        ממתין
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Stack indicator */}
                                {primaryAppointments.length > 1 && (
                                  <div className="absolute top-1 right-1">
                                    <div className="w-4 h-4 bg-white bg-opacity-30 rounded-full flex items-center justify-center text-xs font-bold">
                                      {primaryAppointments.length}
                                    </div>
                                  </div>
                                )}

                                {/* Bottom dots for non-top cards */}
                                {!isTopCard && (
                                  <div className="absolute bottom-1 right-1 flex space-x-1">
                                    {Array.from({ length: Math.min(3, primaryAppointments.length - stackPosition) }).map((_, i) => (
                                      <div key={i} className="w-1 h-1 bg-white bg-opacity-60 rounded-full" />
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        } else if (!isOccupied) {
                          return (
                            <div className="h-full flex items-center justify-center text-xs text-gray-400">
                              פנוי
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}